import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { type ApiErrorResponse, type AppUser } from '../types';

async function meRequest(): Promise<AppUser | null> {
  try {
    const { data } = await apiClient.get<AppUser>('/users/me');
    return data;
  } catch (error: unknown) {
    if ([401, 403].includes((error as ApiErrorResponse).response.status)) {
      return null;
    }
    throw new Error('Failed to load user');
  }
}

async function loginRequest(username: string, password: string): Promise<AppUser> {
  try {
    const { data } = await apiClient.post<AppUser>('/auth/login', { username, password });
    return data;
  } catch (error: unknown) {
    if ((error as ApiErrorResponse).response.status === 401) {
      throw new Error('Invalid username or password');
    }
    throw new Error('Login failed');
  }
}

async function logoutRequest(): Promise<void> {
  await apiClient.post('/auth/logout');
}

type ChangePasswordPayload = {
  currentPassword: string;
  newPassword: string;
};

async function changePasswordRequest(payload: ChangePasswordPayload): Promise<void> {
  try {
    await apiClient.patch('/users/me/password', payload);
  } catch (error: unknown) {
    let message = 'Failed to change password';
    const data = (error as ApiErrorResponse).response.data;
    if (typeof data?.message === 'string') {
      message = data.message;
    } else if (Array.isArray(data?.message) && data.message.length) {
      message = data.message.join(', ');
    }
    throw new Error(message);
  }
}

export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: meRequest, staleTime: 60_000 });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      loginRequest(username, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: logoutRequest,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.clear();
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: ChangePasswordPayload) => changePasswordRequest(payload),
  });
}

export function useRoles(user: AppUser | null | undefined) {
  return {
    isAdminUser: user?.role === 'admin',
    isOperatorUser: user?.role === 'operator',
    isObserverUser: user?.role === 'observer',
  };
}
